import isEmpty from "lodash/isEmpty";
import isNil from "lodash/isNil";

// Short-term / rate-priced products. Per the LoanLock guide (§4) these show
// `lockBuyRate` + `lockRateSpread`; DSCR (and other bond-point-priced loans)
// show `lockPrice`. The backend does NOT enforce this — it's a UI convention
// derived from the field semantics.
export const SHORT_TERM_PRODUCTS = [
  "Fix and Flip",
  "Ground Up Construction",
  "Stabilized Bridge",
  "Foreclosure Bail Out",
  "Vacant Land",
  "Multifamily Bridge",
];

export const PRICE_PRODUCTS = ["DSCR"];

/**
 * Decide which lock-pricing fields to show for a given loan product type.
 * @returns {"price" | "rate" | "both"}
 *   - "price" → show lockPrice only (DSCR / bond-point-priced)
 *   - "rate"  → show lockBuyRate + lockRateSpread (short-term products)
 *   - "both"  → show all three (ambiguous "Other"/unknown product)
 */
export const getLockPricingMode = (productType) => {
  if (PRICE_PRODUCTS.includes(productType)) return "price";
  if (SHORT_TERM_PRODUCTS.includes(productType)) return "rate";
  return "both";
};

/**
 * Compute whether the lock is expired *right now*, client-side. Handles both the
 * new snake_case lock doc and the normalized camelCase view.
 */
export const isLockCurrentlyExpired = (loanLock) => {
  if (isNil(loanLock)) return false;
  const confirmed = loanLock.lock_confirmed ?? loanLock.loanConfirmed;
  if (!confirmed) return false;
  const exp = loanLock.lock_expiration_date ?? loanLock.lockExpirationDate;
  if (isNil(exp)) return false;
  const expiration = new Date(exp);
  if (isNaN(expiration.getTime())) return false;
  return Date.now() > expiration.getTime();
};

// Map a loanProductType to the lock product the /pricing router expects.
// Returns null when ambiguous (the request form then lets the user pick).
export const getLockProduct = (productType) => {
  if (PRICE_PRODUCTS.includes(productType)) return "dscr";
  if (SHORT_TERM_PRODUCTS.includes(productType)) return "rtl";
  return null;
};

// Lifecycle stage off the snake_case lock mirror (deal.loanLock):
//   0 none · 1 requested · 2 confirmed/active · 3 expired
// Tolerates both the new snake_case lock doc and the legacy camelCase fixtures.
export const getLockStage = (lock) => {
  if (isNil(lock) || isEmpty(lock)) return 0;
  const status = lock.lock_status;
  if (status === "Cancelled" || status === "Superseded") return 0;
  if (status === "Expired") return 3;
  if (status === "Requested") return 1;
  const confirmed = lock.lock_confirmed ?? lock.loanConfirmed;
  if (status === "Confirmed" || status === "Repriced" || confirmed) {
    return isLockCurrentlyExpired(lock) ? 3 : 2;
  }
  // present lock, not yet confirmed → treat as requested
  return 1;
};

/**
 * The deal carries a lock from the deal-keyed /pricing store — `deal.loanLock`
 * — that is still the loan's lock: Requested, Confirmed/Repriced or Expired.
 * Since 2026-09-22 that store resolves acquisition deals too, and the inbound
 * Salesforce sync mirrors an acquisition's Rate_Lock__c onto it (2026-10-01).
 * The post-close tool's own store (keyed by Salesforce opportunity) never
 * writes `deal.loanLock`, so such a lock must be shown and managed through the
 * standard lock tab — the post-close panel cannot see it.
 *
 * Expired counts: the hourly sweep flips a lapsed lock to Expired, and the deal
 * store extends an Expired lock (`_extension_lock`, 2026-10-19). Leaving it out
 * handed an acquisition to the post-close panel the moment its lock lapsed —
 * "no active lock", no Extend, and a re-request went to the opportunity-keyed
 * store instead of onto the deal.
 */
export const hasDealLock = (loan) => getLockStage(loan?.loanLock) !== 0;

// Adapt the new snake_case lock doc to the camelCase shape the display tab reads.
// Uses `??` so legacy camelCase fixtures pass through unchanged.
export const normalizeLockToCamel = (lock) => {
  if (isNil(lock) || isEmpty(lock)) return lock;
  return {
    ...lock,
    // Trade counterparty is intentionally NOT surfaced in the drilldown — it's
    // capital-markets-only and the backend strips it for non-CM (see rate-locks.md).
    loanConfirmed: lock.lock_confirmed ?? lock.loanConfirmed,
    lockConfirmationDate: lock.lock_confirm_date ?? lock.lockConfirmationDate,
    lockRequestedDate: lock.lock_requested_date ?? lock.lockRequestedDate,
    lockExpirationDate: lock.lock_expiration_date ?? lock.lockExpirationDate,
    lockNotes: lock.lock_notes ?? lock.lockNotes,
    // Lock type / origination channel. Legacy rows carry only the derived flag —
    // `lockChannelOf` in losLocksApi is what resolves that.
    lockChannel: lock.lock_channel ?? lock.lockChannel,
    // DSCR is bond-priced (points); RTL carries buy rate + spread.
    lockPrice: lock.total_exit_price ?? lock.base_price_for_rate ?? lock.lockPrice,
    lockBuyRate: lock.lock_buy_rate ?? lock.lockBuyRate,
    lockRateSpread: lock.lock_rate_spread ?? lock.lockRateSpread,
    borrowerClassificationCalculated:
      lock.borrower_classification_calculated ??
      lock.borrowerClassificationCalculated,
    borrowerClassificationOverride:
      lock.borrower_classification_override ??
      lock.borrowerClassificationOverride,
    loanSizer: lock.loan_sizer ?? lock.loanSizer,
    loanStatusAtLock: lock.loan_status_at_lock ?? lock.loanStatusAtLock,
    loanTermsAtLock: lock.loan_terms_at_lock ?? lock.loanTermsAtLock,
    lockRequestUser: lock.lock_request_user ?? lock.lockRequestUser,
    lockConfirmUser: lock.lock_confirm_user ?? lock.lockConfirmUser,
  };
};
